<template>
  <div class="sync-container">
    <div v-if="!ready" class="loader">
      <LoadingScreen
        :delay="false"
        :type="currentRelease.is_code_generation_done ? 'default' : 'syncing'"
        theme="dark"
      />
    </div>
    <transition name="fade">
      <div class="wrapper" v-show="ready">
        <!-- HEADER -->
        <SyncHeader v-show="!isFullScreen && !showOnlyCode" @screenChange="reactToScreenChange" />
        <!-- <Header @screenChange="reactToScreenChange" /> -->
        <main ref="mainContent" :style="{ paddingTop: getPaddingTop }" id="sync-content">
          <div class="main-content">
            <MainFrame
              v-show="!showOnlyCode"
              :previewMasterSlug="masterSlug"
              :isPreview="!isReleaseOmni"
              :ready="ready"
            />
            <!-- ARROWS -->
            <ScreenNavArrow side="right" @click="reactToScreenChange" :show="showNavigationArrows" />
            <ScreenNavArrow side="left" @click="reactToScreenChange" :show="showNavigationArrows" />
            <Panels
              :style="{ width: mainContentWidth + 'px' }"
              @generateCode="generateCode"
              v-show="showPanel"
              @toggle="togglePanel"
            />
          </div>
        </main>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { uuid } from '@/utils/uuid';

// import Header from '@/components/OmniView/Header.vue';
import MainFrame from '@/components/OmniView/MainFrame.vue';
import SyncHeader from '@/components/OmniView/Header/SyncHeader.vue';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import ScreenNavArrow from '@/components/OmniView/ScreenNavArrow';
import axios from 'axios';
import { get } from 'lodash-es';
import Panels from '@/components/OmniView/Panels/Panels.vue';

import { isSameRoute, poll } from '@/utils/javascript';
import codegenMixin from '@/components/OmniView/codegenMixin';
import panelMixin from '@/components/OmniView/panelMixin';
import { SocketMixin } from '@/mixins';
import { EventBus } from '@/services/bus';
import { SELECT_OVERRIDE_NODE, SEND_MESSAGE, HIGHLIGHT_COMMENT } from '@/utils/events/omniviewEvents';
import errorHandler from '@/services/errorHandler';

let loadIframe = false;
export default {
  mixins: [codegenMixin, panelMixin, SocketMixin],
  components: {
    // Header,
    SyncHeader,
    MainFrame,
    Panels,
    LoadingScreen,
    ScreenNavArrow
  },
  data() {
    return {
      loading: {
        iframe: true,
        breakpoints: true,
        fetching: true,
        model: false
      },
      masterSlug: ''
    };
  },
  computed: {
    ...mapState('components', { currentComponents: 'items' }),
    ...mapState('components', { currentComponent: 'currentItem' }),
    ...mapState('omniview', { isCompareEnabled: 'isCompareEnabled' }),
    ...mapState('projects', { currentProject: 'currentItem' }),
    ...mapState('omniview', { showOnlyCode: 'showOnlyCode' }),
    ...mapState('omniview', { isFullScreen: 'isFullScreen' }),
    ...mapState('releases', { currentRelease: 'currentItem' }),
    ...mapState('users', { user: 'currentItem' }),
    ...mapGetters({
      activeMode: 'omniview/activeMode',
      currentNode: 'omniview/currentNode',
      currentNodeModel: 'omniview/currentNodeModel',
      domLoading: 'omniview/domLoading',
      currentMasterSlug: 'omniview/currentMasterSlug'
    }),
    getPaddingTop() {
      if (this.isFullScreen) return 0;
      const p = this.isMobile ? 'var(--omniview-topbar-height-mobile)' : 'var(--omniview-topbar-height)';
      return p;
    },
    ready() {
      return !this.loading.fetching;
    },
    showNavigationArrows() {
      return this.currentComponents?.length > 1;
    }
  },
  created() {
    if (this.user.id) {
      if (this.$hj) {
        this.$hj('identify', this.user.id, {
          email: this.user.email
        });
      }
      if (this.$FullStory) {
        this.$FullStory.identify(this.user.id, {
          email: this.user.email
        });
      }
    }
  },
  async mounted() {
    this.selectScreen({});
    this.checkIfSyncAllowed();
    window.addEventListener('message', this.messageListener, false);

    this.$trackEvent('sync-preview.preview.view');

    if (!localStorage.getItem('animp')) {
      this.$tracking.setUserEmail(this.user.email);
      this.$tracking.setUserId(this.user.id);
      this.$tracking.setContext({ latest_paired_design_tool: this.user.latest_paired_design_tool });
      this.$tracking.alias(this.user.id);
    }

    if (!this.showOnlyCode) {
      this.loadData();
    } else {
      this.loading.fetching = false;

      let layerId = get(this.$route, 'query.layer', false);
      let fileId = get(this.$route, 'query.fileId', false);
      let codeLang = get(this.$route, 'query.chromeExtensionLang', false);

      if (codeLang) {
        this.setCodegenLang(codeLang);
      }

      if (fileId && layerId) {
        this.setIsGeneratingCode(true);
        this.generateCodeWithoutIframe({ nodeId: layerId, fileId });
      }

      this.$trackEvent('sync-preview.preview.view');
      let modeCode = this.modes.find((item) => {
        return item.name === 'C';
      });
      this.handleModeChange({
        mode: modeCode,
        fromRoute: false
      });
    }
  },
  destroyed() {
    window.removeEventListener('message', this.messageListener, false);
  },
  methods: {
    ...mapMutations({
      setCodegenLang: 'codePreferences/setCodegenLang',
      setCurrentComponents: 'components/setComponents',
      selectScreen: 'components/setCurrentItem',
      setIsGeneratingCode: 'omniview/setIsGeneratingCode',
      setBreakpoints: 'omniview/setBreakpoints',
      setIsCompareEnabled: 'omniview/setIsCompareEnabled',
      setShowOnlyCode: 'omniview/setShowOnlyCode',
      setActiveBreakpoint: 'omniview/setActiveBreakpoint',
      setDomLoading: 'omniview/setDomLoading',
      setIframeLoading: 'omniview/setIframeLoading',
      setCurrentMasterSlug: 'omniview/setCurrentMasterSlug',
      setIsSyncAllowed: 'omniview/setIsSyncAllowed'
    }),
    ...mapActions({
      fetchProject: 'projects/fetchOne',
      fetchProjectRelease: 'projectReleases/fetchOne',
      fetchRelease: 'releases/fetchOne',
      fetchComponents: 'components/fetchAllOfParent',
      fetchReleaseModel: 'releases/fetchReleaseModel',
      fetchModelFromUrl: 'releases/fetchModelFromUrl'
    }),
    messageListener(e) {
      if (e && e.data && e.data.sender === 'fullScreen') {
        //if the chrome extension wants to change the full screen
        let showOnlyCode = e.data.message.showOnlyCode;
        this.setShowOnlyCode(showOnlyCode);
        return;
      } else if (e && e.data && e.data.sender === 'selectLayer' && e.data.message && e.data.message.layerId) {
        //the chrome extension wants to change the layerId
        let layerId = e.data.message.layerId;
        let fileId = e.data.message.fileId;
        let figma_access = e.data.message.figma_access;

        if (loadIframe) {
          this.checkForNodeInRoute(this.activeMode, layerId);
        } else {
          this.generateCodeWithoutIframe({ nodeId: layerId, fileId, figma_access });
        }
        return;
      } else if (e && e.data === 'iframe-404-load') {
        this.$trackEvent('omniview.preview.not-found', { url: window.location.href });
      }

      if (!e.data.action) return;
      const data = e.data.data;

      switch (e.data.action) {
        case 'dom-load':
          // eslint-disable-next-line
          console.log('DOM-load');
          this.setDomLoading(false);

          if (this.isReleaseOmni) {
            if (this.firstLoad) {
              this.checkForModeInRoute();
            } else {
              this.handleModeChange({
                mode: this.mappedRouteToMode,
                fromRoute: false
              });
            }
          }

          this.firstLoad = false;
          break;

        case 'edit-node':
          {
            let newRoute = { ...this.$route, query: { ...this.$route.query, layer: data.id } };
            if (isSameRoute(this.$route, newRoute)) {
              this.handleCodeModeNodeMessage(data);
            } else {
              this.$router
                .replace(newRoute)
                .then(() => {
                  this.handleCodeModeNodeMessage(data);
                })
                .catch((e) => {
                  errorHandler.captureException(e);
                });
            }
          }
          break;

        case 'escape-clicked':
          this.handleEscapeClicked();
          break;

        default:
          break;
      }
    },
    checkIfSyncAllowed() {
      const { projectShortId } = this.$route.params;
      axios.get(`/rpc/sync/allowed?project_id=${projectShortId}`).then(({ data }) => {
        this.setIsSyncAllowed(data?.result || false);
      });
    },
    checkForNodeInRoute(mode, layerId) {
      // mode here is an object
      let { layer } = '';
      if (layerId) {
        layer = layerId;
      }
      if (!layer) return;
      switch (mode.name) {
        case 'In':
          // no layers in play mode
          break;
        case 'Co':
          // TODO: implement me
          EventBus.$emit(SEND_MESSAGE, {
            action: HIGHLIGHT_COMMENT,
            data: {
              nodeId: layer,
              metadata: {
                source: 'route'
              }
            }
          });
          break;
        case 'C':
          EventBus.$emit(SEND_MESSAGE, {
            action: SELECT_OVERRIDE_NODE,
            data: {
              nodeId: layer,
              metadata: {
                source: 'route'
              }
            }
          });
          break;
        default:
          break;
      }
    },
    async loadData() {
      try {
        this.loading.fetching = true;
        const { projectShortId, releaseShortId } = this.$route.params;
        await this.fetchProjectAndReleases(projectShortId, releaseShortId);

        await this.getBreakpointsFromModel();

        this.loading.fetching = false;
      } catch (error) {
        errorHandler.captureException(error);
        this.loading.fetching = false;
      }
    },
    reactToScreenChange({ fetchBreakpoints }) {
      if (fetchBreakpoints) {
        this.getBreakpointsFromModel(false);
      }
    },
    async fetchProjectAndReleases(projectShortId, releaseShortId) {
      await this.fetchProject({ id: projectShortId });

      await poll({
        fn: () => this.fetchRelease({ id: releaseShortId, skipCache: true }),
        validate: (release) => {
          return release && release.is_code_generation_done;
        },
        interval: 500,
        maxAttempts: 90
      });
      // this.fetchReleaseModel();
    },

    getMasterSlug(model = {}, currentScreen) {
      const similarScreenIds = model.similarScreenIds || [];
      const modelScreens = model.screens || [];
      const regex = new RegExp('screen[0-9]+');
      let masterSlug = '';
      for (let i = 0; i < similarScreenIds.length; i++) {
        const ids = similarScreenIds[i];
        if (ids.includes(currentScreen.modelID)) {
          for (let j = 0; j < ids.length; j++) {
            let id = ids[j];

            const screen = modelScreens.find((s) => s.modelID == id);
            if (!screen) continue;

            if (!masterSlug || masterSlug.length > this.slugsMap[screen.variableID] || regex.test(masterSlug)) {
              masterSlug = this.slugsMap[screen.variableID];
            }
          }
          break;
        }
      }

      if (!masterSlug) {
        masterSlug = this.slugsMap[currentScreen.variableID];
      }

      if (this.currentMasterSlug && masterSlug != this.currentMasterSlug) {
        this.setDomLoading(true);
        this.setIframeLoading(true);
      }
      this.setCurrentMasterSlug(masterSlug);

      return masterSlug;
    },

    async getBreakpointsFromModel() {
      try {
        this.loading.breakpoints = true;
        this.setBreakpoints([]);
        let model,
          currentScreen = null;
        let isProgressiveUpload = false;
        if (this.currentRelease.screens_metadata) {
          isProgressiveUpload = (this.currentRelease.screens_metadata || []).length > 0;
        }

        if (isProgressiveUpload) {
          await this.slugifyScreensNames((this.currentRelease.screens_metadata || []).map((s) => s.variableID));
          const { screenSlug } = this.$route.params;
          currentScreen = this.currentRelease.screens_metadata.find((s) => s.variableID == screenSlug);
          model = this.currentRelease;
          model.screens = model.screens_metadata;
          model.similarScreenIds = model.similar_screen_ids;
        } else {
          let { model: modelTemp, currentScreen: currentScreenTemp } = await this.getModelAndScreen({ slim: true });
          model = modelTemp;
          currentScreen = currentScreenTemp;
        }

        this.setCurrentComponents(model.screens || []);
        this.selectScreen(currentScreen || {});

        this.masterSlug = this.getMasterSlug(model, currentScreen);

        const sizes = [];
        let currentSizeIndex = -1;
        let currentSize = -1;
        for (let i = 0; i < (model.similarScreenIds || []).length; i++) {
          const ids = model.similarScreenIds[i];
          if (ids.includes(currentScreen.modelID)) {
            for (let j = 0; j < ids.length; j++) {
              const id = ids[j];
              if (id == currentScreen.modelID) {
                currentSizeIndex = j;
              }
              const foundScreen = model.screens.find((s) => s.modelID == id);

              if (foundScreen) {
                sizes.push({ width: foundScreen.width, component: foundScreen });
              }
            }
            break;
          }
        }

        const sizesOnly = sizes.map((s) => s.width);

        if (currentSizeIndex != -1) {
          currentSize = sizesOnly[currentSizeIndex];
        }

        sizes.sort((a, b) => (a.width < b.width ? 1 : -1));

        let breakpoints = sizes.map(({ width, component }) => ({ id: uuid(), width, component }));

        if (breakpoints.length == 0) {
          breakpoints = [{ id: uuid(), width: currentScreen.width, component: this.currentComponent }];
        }

        this.setBreakpoints(breakpoints);

        if (currentSize == -1) {
          this.setActiveBreakpoint(breakpoints[0]);
        } else {
          this.setActiveBreakpoint(breakpoints.find((br) => br.width == currentSize));
        }

        this.loading.breakpoints = false;
      } catch (error) {
        this.loading.breakpoints = false;
        this.setBreakpoints([]);
        errorHandler.captureException(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sync-container {
  min-height: 100vh;
  min-width: 100vw;
  height: 100vh;
  width: 100vw;
}

#sync-content {
  position: relative;
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  overflow: hidden;
}

.main-content {
  position: relative;
  flex: 1;
  overflow: hidden;
  background-color: #2d2d2d;
}

.loader {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: var(--secondary);
  // background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
